import { Form, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import SelectComponent from 'components/atoms/SelectComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTableEmise from 'components/molecules/Emise/PrilohyTableEmise';
import FormWrapper from 'components/molecules/FormWrapper';

import identifikaceEmitenta, { IIdentifikaceEmitenta } from 'services/akcionar/emise/identifikaceEmitenta';

import { globalInputRegExp } from 'enums/regExp';
import { ETrueFalse } from 'enums/trueFalse';

import { druhCpSelectDLT } from 'data/codebooks/emise/udaje/druhCpSelect';
import { formaCpSelectAkcie } from 'data/codebooks/emise/udaje/formaCpSelect';
import trueFalseSelect from 'data/codebooks/trueFalseSelect';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import dveDesetinneMistaValidation from 'utils/validations/dveDesetinneMistaValidation';
import numberValidation from 'utils/validations/numberValidation';
import stringLengthValidation from 'utils/validations/stringLengthValidation';

interface IRegistraceDruhCp {
    nextStep: () => void;
    prevStep: () => void;
    zadostID: number;
    setIdentifikacePutData: React.Dispatch<React.SetStateAction<IIdentifikaceEmitenta | undefined>>;
}

const RegistraceDruhCp: React.FC<IRegistraceDruhCp> = ({ nextStep, prevStep, zadostID, setIdentifikacePutData }) => {
    const { cisMeny } = useContext(Context);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [refetch, setRefetch] = useState<boolean>(false);
    const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
    const [data, setData] = useState<IIdentifikaceEmitenta>();

    const saveData = (vals: any) => {
        setIdentifikacePutData(vals);
        return identifikaceEmitenta('PUT', zadostID, vals, 'Registration');
    };

    const onCelkovaHodnotaEmiseChange = () => {
        // pouzite pri vypocte celkovej hodnoty akcie
        const hodnota = (
            (parseFloat(form.getFieldValue('nbti')) || 0) * (parseFloat(form.getFieldValue('mtno')) || 0)
        ).toFixed(2);
        form.setFieldsValue({
            ignore2: formatInputNumberThousand(hodnota)
        });
    };

    useEffect(() => {
        setLoading(true);
        identifikaceEmitenta('get', zadostID, undefined, 'Registration')
            .then(resp => {
                setData(resp);
                if (isFirstRender) {
                    form.setFieldsValue({
                        ...resp,
                        ompr: typeof resp.ompr === 'boolean' ? String(resp.ompr) : undefined
                    });
                    setTimeout(() => onCelkovaHodnotaEmiseChange(), 100);
                    setIsFirstRender(false);
                }
            })
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetch]);

    return (
        <LoadingWrapper loading={loading}>
            <FormWrapper
                outForm={form}
                onFinish={values => {
                    const valsToSend = {
                        ...data,
                        ...values,
                        ...(typeof values.ompr && {
                            ompr: values.ompr === ETrueFalse.TRUE
                        })
                    } as any;
                    if (valsToSend.druhcp !== 'AKCIE') {
                        delete valsToSend.ignore2;
                        delete valsToSend.mtno;
                        delete valsToSend.dnom;
                        delete valsToSend.nbti;
                        delete valsToSend.formacp;
                        delete valsToSend.ompr;
                        delete valsToSend.omprpopis;
                    } else if (!valsToSend.ompr) {
                        delete valsToSend.omprpopis;
                    }
                    setLoading(true);
                    saveData(valsToSend)
                        .then(() => nextStep())
                        .finally(() => setLoading(false));
                }}
                preserve={true}
            >
                <InputComponent
                    label={'ISIN_JINA_JURISDIKCE'}
                    name="cval"
                    type="text"
                    initialValue={data?.cval}
                    capitalize={true}
                    disabled={true}
                />
                <SelectComponent
                    name="druhcp"
                    labelParagraph="ISIN_EMISE_DRUH"
                    isFormItem={true}
                    defaultValue={data?.druhcp}
                    data={druhCpSelectDLT}
                    onChange={rerender}
                />
                <InputComponent
                    initialValue={data?.cfi}
                    label="CFI"
                    name="cfi"
                    maxLength={6}
                    capitalize={true}
                    rules={stringLengthValidation(6, true)}
                />
                <InputComponent initialValue={data?.fisn} label="FISN" name="fisn" />
                {form.getFieldValue('druhcp') === 'AKCIE' && (
                    <>
                        <InputNumberComponent
                            name="mtno"
                            label="ISIN_EMISE_AKCIE_HODNOTA_ZA_CP"
                            onChangeCustom={onCelkovaHodnotaEmiseChange}
                            maxLength={16}
                            precision={2}
                            rules={dveDesetinneMistaValidation(true)}
                            /*   initialValue={
                                udajeEmise?.mtno ||
                                (form.getFieldValue('druhakcie') === EDruhAkcie.PRIORKUS ||
                                form.getFieldValue('druhakcie') === EDruhAkcie.KUS
                                    ? 0
                                    : undefined)
                            } */
                        />
                        <SelectComponent
                            isFormItem={true}
                            name="dnom"
                            // defaultValue={udajeEmise?.dnom}
                            labelParagraph="MENA_JMENOVITEHODNOTY"
                            data={cisMeny}
                        />
                        <InputNumberComponent
                            name="nbti"
                            label="ISIN_EMISE_AKCIE_KUSU"
                            rules={numberValidation(true)}
                            //   initialValue={udajeEmise?.nbti}
                            precision={0}
                            maxLength={20}
                            onChangeCustom={() => {
                                onCelkovaHodnotaEmiseChange();
                            }}
                        />
                        <InputComponent
                            name="ignore2"
                            label="ISIN_EMISE_AKCIE_CELKOVA_HODNOTA"
                            disabled={true}
                            notRequired={true}
                        />
                        <SelectComponent
                            name="formacp"
                            //     defaultValue={udajeEmise?.formacp}
                            isFormItem={true}
                            labelParagraph="ISIN_EMISE_AKCIE_FORMA"
                            data={formaCpSelectAkcie}
                        />
                        <SelectComponent
                            name="ompr"
                            /*   defaultValue={
                                typeof udajeEmise?.ompr === 'boolean' ? String(udajeEmise.ompr) : udajeEmise?.ompr
                            } */
                            isFormItem={true}
                            labelParagraph="ISIN_EMISE_AKCIE_PREVODITELNOST"
                            infoTooltipText={'ISIN_EMISE_AKCIE_PREVODITELNOST_TOOLTIP'}
                            data={trueFalseSelect}
                            onChange={rerender}
                        />
                        {form.getFieldValue('ompr') === 'true' && (
                            <InputComponent
                                label="ISIN_EMISE_AKCIE_PREVODITELNOST_POPIS"
                                name="omprpopis"
                                //   initialValue={udajeEmise?.omprpopis}
                                maxLength={500}
                                rules={[
                                    () => ({
                                        validator(_, value: string) {
                                            if (!value || globalInputRegExp.test(value)) return Promise.resolve();
                                            return Promise.reject(<TranslateText>VALIDACE_GLOBAL_INPUT</TranslateText>);
                                        }
                                    })
                                ]}
                            />
                        )}
                    </>
                )}
                <PrilohyTableEmise
                    zadostID={zadostID}
                    required={true}
                    setRefetch={setRefetch}
                    prilohyData={data?.prilohy || []}
                />

                <Row className="button-row">
                    <ButtonComponent buttontext="ZPET" onClick={() => prevStep()} type="default" id="isinZpetbtn" />
                    <ButtonComponent buttontext="POKRACOVAT" id="isinPokracovatBtn" htmlType="submit" />
                </Row>
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default RegistraceDruhCp;
